// Agent-Tender API client.
//
// Isolated from the 6800-line services/api.js on purpose (smaller PR surface,
// no risk to the shared instance). Two axios instances:
//   • authApi   — Bearer token from persisted auth, for agency-member endpoints
//   • publicApi — no auth, for the public verify endpoints (Rule: club never
//                 needs an account to confirm)
//
// Mock switch (D-007): when REACT_APP_AGENT_TENDER_MOCK === "1" every call is
// served from services/agentTenderMock.js instead of the network, so the whole
// UI works before the backend lands. Real wiring is already in place for when
// the flag is off.
import axios from "axios";
import agentTenderMock from "./agentTenderMock";

const API_BASE_URL = process.env.REACT_APP_API_ENDPOINT;
// Mock layer is active ONLY when REACT_APP_AGENT_TENDER_MOCK === "1" (default
// off). Kept isolated so the real wiring ships untouched once the backend lands.
const USE_MOCK = process.env.REACT_APP_AGENT_TENDER_MOCK === "1";

const authApi = axios.create({ baseURL: API_BASE_URL, withCredentials: true });

// Attach the Bearer token the same way the main client persists it
// (authSlice writes localStorage "tokens" = { access, refresh }).
authApi.interceptors.request.use((config) => {
  try {
    const tokens = JSON.parse(localStorage.getItem("tokens") || "null");
    if (tokens?.access) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${tokens.access}`;
    }
  } catch (e) {
    /* no token available — request proceeds and the server returns 401 */
  }
  return config;
});

// Public instance: no credentials, but it DOES attach a Bearer token when one
// is around (best-effort). The public verify views carry no authentication
// classes on purpose (a stale JWT must never 401 the club contact); the
// backend probes the header tolerantly so a signed-in club member gets the
// one-click confirm state (signed_in_member on the token check) while everyone
// else stays anonymous.
const publicApi = axios.create({ baseURL: API_BASE_URL });
publicApi.interceptors.request.use((config) => {
  try {
    const tokens = JSON.parse(localStorage.getItem("tokens") || "null");
    if (tokens?.access) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${tokens.access}`;
    }
  } catch (e) {
    /* anonymous — the public endpoints work without a token */
  }
  return config;
});

// DRF validation errors arrive as {field: ["msg", ...], ...} without any
// detail/message/error key. Surface the first field message instead of the
// generic fallback, so a 400 is actionable ("min_age: A valid integer is
// required.") rather than "Something went wrong".
const firstFieldError = (data) => {
  if (!data || typeof data !== "object" || Array.isArray(data)) return null;
  for (const [field, messages] of Object.entries(data)) {
    const msg = Array.isArray(messages) ? messages[0] : messages;
    if (typeof msg === "string" && msg) {
      return field === "non_field_errors" ? msg : `${field}: ${msg}`;
    }
  }
  return null;
};

// Uniform error shaping so callers always get {success,data,error[,status]}.
// `data` carries the raw response body: structured 400s (e.g. the club-name
// guard's {code:'club_name_in_text', warnings:[...]}) need more than a string.
const fail = (error) => ({
  success: false,
  status: error?.response?.status,
  data: error?.response?.data,
  error:
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    firstFieldError(error?.response?.data) ||
    "Something went wrong. Please try again.",
});

const listPagination = (data, params) => ({
  count: data?.count ?? (Array.isArray(data?.results) ? data.results.length : 0),
  next: data?.next ?? null,
  previous: data?.previous ?? null,
  currentPage: parseInt(
    (params instanceof URLSearchParams ? params.get("page") : params?.page) || "1",
    10,
  ),
});

export const agentTenderService = {
  getFeed: async (params, signal) => {
    if (USE_MOCK) return agentTenderMock.getFeed(params);
    try {
      const res = await authApi.get("/dashboard/agent-tenders/", { params, signal });
      return {
        success: true,
        data: res.data.results || res.data || [],
        pagination: listPagination(res.data, params),
        // Per-tab totals for the feed tab bar (Professional/Youth/Staff). Only
        // the list endpoint returns this; null when absent (e.g. mock/older BE).
        tabCounts: res.data.tab_counts || null,
      };
    } catch (error) {
      if (error?.name === "CanceledError") throw error;
      return fail(error);
    }
  },

  getFeedOne: async (id) => {
    if (USE_MOCK) return agentTenderMock.getFeedOne(id);
    try {
      const res = await authApi.get(`/dashboard/agent-tenders/${id}/`);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  getMine: async (params, signal) => {
    if (USE_MOCK) return agentTenderMock.getMine(params);
    try {
      const res = await authApi.get("/dashboard/agent-tenders/mine/", { params, signal });
      return {
        success: true,
        data: res.data.results || res.data || [],
        pagination: listPagination(res.data, params),
        tabCounts: res.data.tab_counts || null,
      };
    } catch (error) {
      if (error?.name === "CanceledError") throw error;
      return fail(error);
    }
  },

  // Club picker for the create form (D-008): [{id, name, country, logo}].
  getClubs: async (q = "", signal) => {
    if (USE_MOCK) return agentTenderMock.getClubs(q);
    try {
      const res = await authApi.get("/dashboard/agent-tenders/clubs/", {
        params: { q },
        signal,
      });
      return { success: true, data: res.data.results || res.data || [] };
    } catch (error) {
      if (error?.name === "CanceledError") throw error;
      return fail(error);
    }
  },

  // V2 target-window choices for the create form's period selector: current +
  // next windows for the picked club's country. Pass EITHER {club_id} (on-
  // platform) or {wyscout_club_id} (catalog pick — resolved WITHOUT creating a
  // club). Response: {country, choices:[{value,label,opens_at,closes_at}]}.
  getWindowChoices: async (params, signal) => {
    if (USE_MOCK)
      return agentTenderMock.getWindowChoices
        ? agentTenderMock.getWindowChoices(params)
        : { success: true, data: { country: "", choices: [] } };
    try {
      const res = await authApi.get("/dashboard/agent-tenders/window-choices/", {
        params,
        signal,
      });
      return { success: true, data: res.data };
    } catch (error) {
      if (error?.name === "CanceledError") throw error;
      return fail(error);
    }
  },

  create: async (payload) => {
    if (USE_MOCK) return agentTenderMock.create(payload);
    try {
      const res = await authApi.post("/dashboard/agent-tenders/", payload);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  update: async (id, payload) => {
    if (USE_MOCK) return agentTenderMock.update(id, payload);
    try {
      const res = await authApi.patch(`/dashboard/agent-tenders/${id}/`, payload);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  close: async (id) => {
    if (USE_MOCK) return agentTenderMock.close(id);
    try {
      const res = await authApi.post(`/dashboard/agent-tenders/${id}/close/`);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // Save / unsave a request (P6). Source-agnostic SavedTender endpoints — they
  // accept agent-requests too (backend now DB-unique, so both are idempotent).
  // The Saved feed is the anonymised agent feed with ?saved=true (never the
  // club-serializer /dashboard/tenders/saved/, which would leak the club).
  save: async (id) => {
    if (USE_MOCK) return agentTenderMock.save ? agentTenderMock.save(id) : { success: true };
    try {
      const res = await authApi.post(`/dashboard/tenders/${id}/save_tender/`);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  unsave: async (id) => {
    if (USE_MOCK) return agentTenderMock.unsave ? agentTenderMock.unsave(id) : { success: true };
    try {
      const res = await authApi.post(`/dashboard/tenders/${id}/unsave_tender/`);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // NOTE: no phone field here (Brief Rule 10) — only the club contact email.
  // Arms a request's verification: mints the signed token (QR + link) and,
  // ONLY when clubContactEmail is non-empty, also sends the club email — the
  // address is optional (design doc 03 screen 6 / G-1). The key is ALWAYS sent,
  // blank included: a blank value states "no email way", while omitting it
  // would mean "use the club's stored contact" and could mail behind the
  // agent's back. verificationToken echoes the token from a previous arm so
  // the backend reuses that verification instead of minting a competing one.
  requestVerification: async (id, clubContactEmail, verificationToken) => {
    if (USE_MOCK) return agentTenderMock.requestVerification(id, clubContactEmail);
    try {
      const payload = { club_contact_email: clubContactEmail || "" };
      if (verificationToken) payload.verification_token = verificationToken;
      const res = await authApi.post(
        `/dashboard/agent-tenders/${id}/request-verification/`,
        payload,
      );
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // One-click confirm for a signed-in member of the request's club (V2,
  // design 03 screen 9). Authed — the session IS the identity, no OTP.
  confirmInPlatform: async (id) => {
    if (USE_MOCK)
      return agentTenderMock.confirmInPlatform
        ? agentTenderMock.confirmInPlatform(id)
        : { success: true, data: { verification_status: "club_verified" } };
    try {
      const res = await authApi.post(
        `/dashboard/agent-tenders/${id}/confirm-inplatform/`,
      );
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // Owner-side confirmation certificate PDF (V2, design doc 04). Authed and
  // independent of the verify token TTL — works while active AND after the
  // request is archived, as long as it was club-verified.
  downloadCertificate: async (id) => {
    if (USE_MOCK) return { success: false, error: "Not available in mock mode." };
    try {
      const res = await authApi.get(`/dashboard/agent-tenders/${id}/certificate/`, {
        responseType: "blob",
      });
      return { success: true, blob: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // ── Phase 1.5: response pipeline ──────────────────────────────────────────

  // Respond to another agency's tender with a player from your portfolio.
  // The responding side never sees the club (anonymity) — this posts a Pitch
  // against a source=agent tender.
  pitch: async (id, payload) => {
    if (USE_MOCK) return agentTenderMock.pitch(id, payload);
    try {
      const res = await authApi.post(`/dashboard/agent-tenders/${id}/pitch/`, payload);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // Posting side: all responses received on your agency's tenders (club + player
  // fully visible, Rule 8). Filter by tender, status, shortlisted.
  getResponses: async (params, signal) => {
    if (USE_MOCK) return agentTenderMock.getResponses(params);
    try {
      const res = await authApi.get("/dashboard/agent-tenders/responses/", {
        params,
        signal,
      });
      return {
        success: true,
        data: res.data.results || res.data || [],
        pagination: listPagination(res.data, params),
      };
    } catch (error) {
      if (error?.name === "CanceledError") throw error;
      return fail(error);
    }
  },

  // Posting side: accept or reject a response. `status` is "accepted" or
  // "rejected" (the exact value the backend PATCH expects). Accept opens the
  // anonymized chat channel first, so a 503 means the channel failed and the
  // status did NOT change. On accept the response body carries
  // { channel: { channel_id } } for the chat deep-link.
  setResponseStatus: async (pitchId, status) => {
    if (USE_MOCK) return agentTenderMock.setResponseStatus(pitchId, status);
    try {
      const res = await authApi.patch(
        `/dashboard/agent-tenders/responses/${pitchId}/status/`,
        { status },
      );
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // Posting side: toggle a response onto the shortlist (SavedPitch).
  setResponseShortlist: async (pitchId, on) => {
    if (USE_MOCK) return agentTenderMock.setResponseShortlist(pitchId, on);
    try {
      const url = `/dashboard/agent-tenders/responses/${pitchId}/shortlist/`;
      const res = on ? await authApi.post(url) : await authApi.delete(url);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },
};

// `channel` ("link" | "qr") is threaded through as a ?c= query param so the
// public endpoints can log how the club arrived. It comes from the ?c= param on
// the verification URL the agent shared (link_url vs qr_url).
const withChannel = (path, channel) =>
  channel ? `${path}?c=${encodeURIComponent(channel)}` : path;

const VERIFY_BASE = "/dashboard/public/agent-tenders/verify";

export const agentTenderPublicService = {
  getVerify: async (token, channel) => {
    if (USE_MOCK) return agentTenderMock.getVerify(token);
    try {
      const res = await publicApi.get(
        withChannel(`${VERIFY_BASE}/${encodeURIComponent(token)}/`, channel),
      );
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // V2 identity step: the club contact's name + role travel WITH the phone and
  // confirm calls (backend stores them in the verification audit trail).
  submitPhone: async (token, phone, channel, identity = {}) => {
    if (USE_MOCK) return agentTenderMock.submitPhone(token, phone);
    try {
      const res = await publicApi.post(
        withChannel(`${VERIFY_BASE}/${encodeURIComponent(token)}/phone/`, channel),
        {
          phone,
          confirmer_name: identity.confirmer_name || "",
          confirmer_role: identity.confirmer_role || "",
        },
      );
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  confirmOtp: async (token, otp, channel, identity = {}) => {
    if (USE_MOCK) return agentTenderMock.confirmOtp(token, otp);
    try {
      const res = await publicApi.post(
        withChannel(`${VERIFY_BASE}/${encodeURIComponent(token)}/confirm/`, channel),
        {
          otp,
          confirmer_name: identity.confirmer_name || "",
          confirmer_role: identity.confirmer_role || "",
        },
      );
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // One-tap fraud report (V2, design 03 screen 9b step 6): poster, request
  // ref, channel and audit history are compiled server-side; the optional
  // note + reporter identity are the only client inputs. Throttled 5/h.
  reportFraud: async (token, { note, reporter_name, reporter_role } = {}, channel) => {
    if (USE_MOCK) return { success: true, data: { status: "reported" } };
    try {
      const res = await publicApi.post(
        withChannel(
          `${VERIFY_BASE}/${encodeURIComponent(token)}/report-fraud/`,
          channel,
        ),
        {
          note: note || "",
          reporter_name: reporter_name || "",
          reporter_role: reporter_role || "",
        },
      );
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // Club-side confirmation certificate PDF (token-gated; 409 pre-confirm).
  // A plain URL: the token is the capability, so an anchor download works.
  certificateUrl: (token) =>
    `${API_BASE_URL}${VERIFY_BASE}/${encodeURIComponent(token)}/certificate/`,

  decline: async (token, reason, channel) => {
    if (USE_MOCK) return agentTenderMock.decline(token, reason);
    try {
      const res = await publicApi.post(
        withChannel(`${VERIFY_BASE}/${encodeURIComponent(token)}/confirm/`, channel),
        { decline: true, reason: reason || "" },
      );
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },
};

export const AGENT_TENDER_MOCK_ENABLED = USE_MOCK;
